/**
 * WhatsAppSendResultModal — shared modal that renders progress + per-recipient
 * outcomes for an in-flight WhatsApp drawdown send. Used by Drawdown Emails,
 * Reminders, and Acknowledgments tabs.
 */
import React, { useMemo } from 'react';
import { Loader2, MessageCircle, X } from 'lucide-react';

const WA_STATUS_META = {
  queued:    { cls: 'bg-gray-100 text-gray-600',     label: 'Queued' },
  sent:      { cls: 'bg-blue-100 text-blue-700',     label: 'Sent' },
  delivered: { cls: 'bg-green-100 text-green-700',   label: 'Delivered' },
  read:      { cls: 'bg-emerald-100 text-emerald-800', label: 'Read' },
  failed:    { cls: 'bg-red-100 text-red-700',       label: 'Failed' },
  skipped:   { cls: 'bg-yellow-100 text-yellow-700', label: 'Skipped' },
};

const SKIP_REASON_LABELS = {
  opted_out:           'Opted out',
  no_number:           'No number',
  unresolved_variable: 'Unresolved variable',
  no_template:         'No template',
  no_notice_pdf:       'No notice PDF',
};

const WhatsAppSendResultModal = ({ job, onClose }) => {
  const totals = job.totals || {};
  const recipients = job.recipients || [];
  const skippedBreakdown = useMemo(() => {
    const counts = {};
    recipients.filter((r) => r.status === 'skipped').forEach((r) => {
      const k = r.skip_reason || 'other';
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [recipients]);

  const isProcessing = job.status === 'processing' || job.status === 'queued';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="dd-wa-send-result-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900 flex items-center">
            <MessageCircle size={16} className="text-green-600 mr-2" />
            WhatsApp send result · {isProcessing && <Loader2 size={12} className="animate-spin mx-2 text-blue-600" />}
            <span className="ml-1 font-normal text-gray-600">{job.provider_template_name}</span>
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="dd-wa-result-close"><X size={16} /></button>
        </div>

        <div className="px-5 py-4 grid grid-cols-3 sm:grid-cols-6 gap-2 text-center text-xs border-b border-gray-100">
          <div className="bg-gray-50 rounded p-2"><p className="font-bold text-gray-900">{job.total || 0}</p><p className="text-gray-500">Accounts</p></div>
          <div className="bg-blue-50 rounded p-2" data-testid="dd-wa-totals-sent"><p className="font-bold text-blue-700">{totals.sent || 0}</p><p className="text-gray-500">Sent</p></div>
          <div className="bg-green-50 rounded p-2" data-testid="dd-wa-totals-delivered"><p className="font-bold text-green-700">{totals.delivered || 0}</p><p className="text-gray-500">Delivered</p></div>
          <div className="bg-emerald-50 rounded p-2" data-testid="dd-wa-totals-read"><p className="font-bold text-emerald-700">{totals.read || 0}</p><p className="text-gray-500">Read</p></div>
          <div className="bg-yellow-50 rounded p-2" data-testid="dd-wa-totals-skipped"><p className="font-bold text-yellow-700">{totals.skipped || 0}</p><p className="text-gray-500">Skipped</p></div>
          <div className="bg-red-50 rounded p-2" data-testid="dd-wa-totals-failed"><p className="font-bold text-red-700">{totals.failed || 0}</p><p className="text-gray-500">Failed</p></div>
        </div>

        {Object.keys(skippedBreakdown).length > 0 && (
          <div className="px-5 py-2 border-b border-gray-100 flex flex-wrap gap-2 text-xs" data-testid="dd-wa-skipped-breakdown">
            <span className="text-gray-600 font-medium">Skipped breakdown:</span>
            {Object.entries(skippedBreakdown).map(([k, v]) => (
              <span key={k} className="bg-yellow-50 text-yellow-800 px-2 py-0.5 rounded border border-yellow-200">
                {SKIP_REASON_LABELS[k] || k}: <strong>{v}</strong>
              </span>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-3">
          <table className="w-full text-xs">
            <thead className="text-left text-gray-500 border-b border-gray-100">
              <tr>
                <th className="py-1.5 pr-2">Account / Contact</th>
                <th className="py-1.5 pr-2">Phone</th>
                <th className="py-1.5 pr-2">Status</th>
                <th className="py-1.5 pr-2">Detail</th>
              </tr>
            </thead>
            <tbody>
              {recipients.length === 0 ? (
                <tr><td colSpan={4} className="py-4 text-center text-gray-500"><em>No recipients yet — give it a moment...</em></td></tr>
              ) : recipients.map((r) => {
                const meta = WA_STATUS_META[r.status] || WA_STATUS_META.queued;
                return (
                  <tr key={r.id} className="border-b border-gray-100 last:border-0" data-testid={`dd-wa-recipient-${r.id}`}>
                    <td className="py-1.5 pr-2">
                      <div className="font-medium text-gray-900 truncate">{r.account_name}</div>
                      <div className="text-gray-500">{r.contact_name}</div>
                    </td>
                    <td className="py-1.5 pr-2 font-mono text-gray-700">
                      {r.country_code || ''}{r.phone_number || (r.skip_reason === 'no_number' ? <em className="text-gray-400">—</em> : '')}
                    </td>
                    <td className="py-1.5 pr-2">
                      <span className={`px-2 py-0.5 rounded-full font-medium ${meta.cls}`}>{meta.label}</span>
                    </td>
                    <td className="py-1.5 pr-2 text-gray-600 max-w-[14rem]">
                      {r.skip_reason && (
                        <span className="block text-yellow-700">
                          {SKIP_REASON_LABELS[r.skip_reason] || r.skip_reason}{r.skip_detail ? ` — ${r.skip_detail}` : ''}
                        </span>
                      )}
                      {r.error_detail && <span className="block text-red-600 truncate" title={r.error_detail}>{r.error_detail}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
            data-testid="dd-wa-result-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSendResultModal;
export { WhatsAppSendResultModal };
